import { logError, logInfo, logWarn } from './logger';

const PUSH_SW_PATH = '/push-sw.js';

// Comentario em pt-BR: essa chave publica fica no frontend porque o navegador
// precisa dela para criar a assinatura Web Push do device.
const env: ImportMetaEnv | Record<string, string> = typeof import.meta !== 'undefined' ? import.meta.env : {};
const publicPushKey = env.VITE_WEB_PUSH_PUBLIC_KEY || '';

export type PushAvailability =
  | { ok: true }
  | {
      ok: false;
      reason:
        | 'unsupported'
        | 'missing-config'
        | 'not-secure-context'
        | 'ios-requires-install';
      title: string;
      message: string;
    };

export type PushSubscribeResult =
  | {
      success: true;
      subscription: PushSubscription;
    }
  | {
      success: false;
      title: string;
      message: string;
      debug?: Record<string, unknown>;
    };

const normalizePublicPushKey = (rawKey: string) => {
  const trimmed = rawKey.trim();
  const withoutPrefix = trimmed.includes('=')
    ? trimmed.slice(trimmed.lastIndexOf('=') + 1)
    : trimmed;

  return withoutPrefix.replace(/^["']+|["']+$/g, '').replace(/\s+/g, '');
};

// Comentario em pt-BR: eu troquei a conversao para ela tambem validar a chave
// publica antes do subscribe. Isso evita erro chato quando a env vem com espaco,
// aspas ou ate com o prefixo VITE_WEB_PUSH_PUBLIC_KEY= colado.
const urlBase64ToUint8Array = (base64String: string) => {
  const normalizedKey = normalizePublicPushKey(base64String);
  const padding = '='.repeat((4 - (normalizedKey.length % 4)) % 4);
  const base64 = (normalizedKey + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }

  return outputArray;
};

const getPublicPushKeyDiagnostics = () => {
  try {
    const normalizedKey = normalizePublicPushKey(publicPushKey);
    const bytes = urlBase64ToUint8Array(normalizedKey);

    return {
      rawLength: publicPushKey.length,
      normalizedLength: normalizedKey.length,
      byteLength: bytes.length,
      firstByte: bytes[0] ?? null,
      valid: bytes.length === 65 && bytes[0] === 4
    };
  } catch (error) {
    const normalizedError = error as { message?: string };
    return {
      rawLength: publicPushKey.length,
      normalizedLength: normalizePublicPushKey(publicPushKey).length,
      byteLength: 0,
      firstByte: null,
      valid: false,
      errorMessage: normalizedError.message || 'invalid-key'
    };
  }
};

export const isPushSupported = () =>
  typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window;

export const isPushConfigured = () => normalizePublicPushKey(publicPushKey).length > 0;

export const isStandaloneDisplayMode = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const mediaMatch = window.matchMedia?.('(display-mode: standalone)').matches;
  const navigatorStandalone = typeof navigator !== 'undefined' && 'standalone' in navigator
    ? Boolean((navigator as Navigator & { standalone?: boolean }).standalone)
    : false;

  return Boolean(mediaMatch || navigatorStandalone);
};

export const isIosDevice = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

export const getBrowserName = () => {
  if (typeof navigator === 'undefined') {
    return 'desconhecido';
  }

  const userAgent = navigator.userAgent;

  if (/EdgiOS|Edg\//.test(userAgent)) {
    return 'edge';
  }

  if (/OPiOS|OPR\//.test(userAgent)) {
    return 'opera';
  }

  if (/CriOS|Chrome\//.test(userAgent) && !/EdgiOS|Edg\//.test(userAgent)) {
    return 'chrome';
  }

  if (/FxiOS|Firefox\//.test(userAgent)) {
    return 'firefox';
  }

  if (/Safari\//.test(userAgent) && !/CriOS|Chrome|FxiOS|Firefox|EdgiOS|Edg\//.test(userAgent)) {
    return 'safari';
  }

  return 'desconhecido';
};

export const isMobileSafariWithoutInstall = () => (
  isIosDevice()
  && getBrowserName() === 'safari'
  && !isStandaloneDisplayMode()
);

export const getPushAvailability = (): PushAvailability => {
  if (!isPushConfigured()) {
    return {
      ok: false,
      reason: 'missing-config',
      title: 'Push nao configurado',
      message: 'A chave publica do push ainda nao chegou no frontend deste ambiente.'
    };
  }

  const keyDiagnostics = getPublicPushKeyDiagnostics();
  if (!keyDiagnostics.valid) {
    return {
      ok: false,
      reason: 'missing-config',
      title: 'Chave push invalida',
      message: 'A chave publica do push deste ambiente veio em formato invalido. Revise a env VITE_WEB_PUSH_PUBLIC_KEY.'
    };
  }

  if (typeof window !== 'undefined' && !window.isSecureContext) {
    return {
      ok: false,
      reason: 'not-secure-context',
      title: 'Conexao insegura',
      message: 'Notificacoes push exigem HTTPS real para funcionar neste device.'
    };
  }

  if (!isPushSupported()) {
    return {
      ok: false,
      reason: 'unsupported',
      title: 'Push indisponivel',
      message: 'Esse navegador nao suporta notificacoes push neste device.'
    };
  }

  if (isMobileSafariWithoutInstall()) {
    return {
      ok: false,
      reason: 'ios-requires-install',
      title: 'Instale o app no iPhone',
      message: 'No iPhone, o push so funciona com o app aberto pela Tela de Inicio. Use Compartilhar > Adicionar a Tela de Inicio e depois tente novamente.'
    };
  }

  return { ok: true };
};

export const getPushDebugSnapshot = () => ({
  secureContext: typeof window !== 'undefined' ? window.isSecureContext : false,
  standalone: isStandaloneDisplayMode(),
  iosDevice: isIosDevice(),
  browserName: getBrowserName(),
  pushSupported: isPushSupported(),
  pushConfigured: isPushConfigured(),
  publicKey: getPublicPushKeyDiagnostics(),
  notificationPermission: typeof Notification !== 'undefined' ? Notification.permission : 'unavailable',
  hasServiceWorker: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
  hasPushManager: typeof window !== 'undefined' && 'PushManager' in window
});

export const registerPushServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!isPushSupported()) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register(PUSH_SW_PATH);
    logInfo('push', 'Service worker de push registrado.', { scope: registration.scope });
    return registration;
  } catch (error) {
    logError('push', 'Falha ao registrar service worker de push.', error);
    return null;
  }
};

export const getPushPermissionState = (): NotificationPermission => {
  if (!isPushSupported()) {
    return 'denied';
  }

  return Notification.permission;
};

export const getCurrentPushSubscription = async (): Promise<PushSubscription | null> => {
  if (!isPushSupported()) {
    return null;
  }

  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
};

export const requestPushPermissionAndSubscribe = async (): Promise<PushSubscribeResult> => {
  const availability = getPushAvailability();
  if (!availability.ok) {
    logWarn('push', 'Push indisponivel para assinatura neste contexto.', availability);
    return {
      success: false,
      title: availability.title,
      message: availability.message,
      debug: getPushDebugSnapshot()
    };
  }

  const registration = await registerPushServiceWorker();
  if (!registration) {
    return {
      success: false,
      title: 'Service worker indisponivel',
      message: 'Nao deu para registrar a base de notificacoes deste app no navegador.',
      debug: getPushDebugSnapshot()
    };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    logWarn('push', 'Permissao de push nao foi concedida.', { permission });
    return {
      success: false,
      title: 'Permissao nao concedida',
      message: permission === 'denied'
        ? 'O navegador bloqueou as notificacoes. Libere essa permissao e tente de novo.'
        : 'A permissao de notificacoes nao foi concluida neste device.',
      debug: {
        ...getPushDebugSnapshot(),
        permission
      }
    };
  }

  const existingSubscription = await registration.pushManager.getSubscription();
  if (existingSubscription) {
    return {
      success: true,
      subscription: existingSubscription
    };
  }

  try {
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicPushKey)
    });

    logInfo('push', 'Assinatura Web Push criada no device.');
    return {
      success: true,
      subscription
    };
  } catch (error) {
    logError('push', 'Falha ao assinar Web Push.', error);
    const normalizedError = error as { name?: string; message?: string };
    return {
      success: false,
      title: 'Falha ao assinar o push',
      message: normalizedError.message
        ? `O navegador recusou a assinatura push: ${normalizedError.message}`
        : 'O navegador nao confirmou a assinatura push deste device.',
      debug: {
        ...getPushDebugSnapshot(),
        errorName: normalizedError.name || 'unknown',
        errorMessage: normalizedError.message || 'unknown'
      }
    };
  }
};

export const unsubscribeCurrentPush = async (): Promise<{ success: boolean; endpoint?: string | null }> => {
  const subscription = await getCurrentPushSubscription();
  if (!subscription) {
    return { success: true, endpoint: null };
  }

  const endpoint = subscription.endpoint;
  const success = await subscription.unsubscribe();
  if (!success) {
    logWarn('push', 'O navegador nao confirmou o unsubscribe do push.', { endpoint });
  }

  return { success, endpoint };
};

export const serializePushSubscription = (subscription: PushSubscription) => {
  const json = subscription.toJSON();
  return {
    endpoint: subscription.endpoint,
    p256dh: json.keys?.p256dh || '',
    auth: json.keys?.auth || '',
    expirationTime: subscription.expirationTime || null
  };
};
